import { ScrollArea } from "@frontend/components/ui/scroll-area"
import { useApiQuery } from "@frontend/hooks/use-api"
import { cn } from "@frontend/lib/utils"
import { FolderOpen, LayoutTemplate, Settings, Users } from "lucide-react"
import { NavLink } from "react-router"

interface SidebarNavItem {
  key: string
  to: string
  label: string
  icon: "projects" | "members" | "templates" | "settings"
}

function NavIcon({ icon }: { icon: SidebarNavItem["icon"] }) {
  if (icon === "projects") return <FolderOpen className="size-4 shrink-0" />
  if (icon === "members") return <Users className="size-4 shrink-0" />
  if (icon === "templates") return <LayoutTemplate className="size-4 shrink-0" />
  return <Settings className="size-4 shrink-0" />
}

export function SidebarView({ navItems }: { navItems: SidebarNavItem[] }) {
  return (
    <aside className="flex flex-col h-full bg-sidebar border-r border-sidebar-border overflow-hidden">
      <ScrollArea className="flex-1">
        <nav className="flex flex-col gap-0.5 p-2 pt-3">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors w-full text-left whitespace-nowrap overflow-hidden",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-foreground"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )
              }
            >
              <NavIcon icon={item.icon} />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  )
}

export function SidebarContainer() {
  const { data: currentUser } = useApiQuery<{
    id: string
    instance_role: "admin" | "member" | null
  }>(["auth", "me"], "/auth/me")
  const isInstanceAdmin = currentUser?.instance_role === "admin"

  const navItems: SidebarNavItem[] = [
    {
      key: "projects",
      to: "/",
      label: "Projects",
      icon: "projects"
    },
    ...(isInstanceAdmin
      ? [
          {
            key: "members",
            to: "/members",
            label: "Members",
            icon: "members" as const
          },
          {
            key: "templates",
            to: "/templates",
            label: "Templates",
            icon: "templates" as const
          }
        ]
      : []),
    {
      key: "settings",
      to: "/settings",
      label: "Settings",
      icon: "settings"
    }
  ]

  return <SidebarView navItems={navItems} />
}

export const Sidebar = SidebarContainer
